import React, { useEffect } from "react";
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import { ErrorBoundary } from "./components/common/ErrorBoundary";
import { Header } from "./components/common/Header";
import { Sidebar } from "./components/common/Sidebar";
import { ProtectedRoute } from "./components/auth/ProtectedRoute";
import { FleetScopedRoute } from "./components/auth/FleetScopedRoute";
import { AdminOnlyRoute } from "./components/auth/AdminOnlyRoute";
import { useAppStore } from "./store/useAppStore";
import { useRealtime } from "./hooks/useRealtime";
import { useResourceBootstrap } from "./hooks/useResourceBootstrap";
import { useAuth } from "./hooks/useAuth";
import { StatusPage } from "./components/status/StatusPage";
import { getViewFromPath, routeElements } from "./routes/appRoutes";
import { getDefaultPathForRole } from "./utils/authRoles";
import "./App.css";

const AuthenticatedAppShell: React.FC = () => {
  const location = useLocation();
  const setCurrentView = useAppStore((state) => state.setCurrentView);
  const { isFleetScoped } = useAuth();

  useResourceBootstrap();
  useRealtime();

  useEffect(() => {
    const view = getViewFromPath(location.pathname);
    setCurrentView(view);
  }, [location.pathname, setCurrentView]);

  const view = getViewFromPath(location.pathname);
  const content = routeElements[view] ?? routeElements.dashboard;
  const isFullHeight = ["map", "history"].includes(view);

  return (
    <div className="h-screen w-screen bg-gray-50 flex overflow-hidden">
      {!isFleetScoped && <Sidebar />}

      <div className="flex-1 flex flex-col min-w-0">
        <Header />

        <main className="flex-1 overflow-hidden">
          {isFullHeight ? (
            <div className="h-full w-full">{content}</div>
          ) : (
            <div className="h-full overflow-y-auto">
              <div className="px-4 py-2">{content}</div>
            </div>
          )}
        </main>
      </div>
      <Outlet />
    </div>
  );
};

const RoleHomeRedirect: React.FC = () => {
  const { user } = useAuth();
  return <Navigate to={getDefaultPathForRole(user?.role)} replace />;
};

const App: React.FC = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <Routes>
          <Route path="/status" element={<StatusPage />} />
          <Route
            element={
              <ProtectedRoute>
                <Outlet />
              </ProtectedRoute>
            }
          >
            <Route element={<FleetScopedRoute />}>
              <Route element={<AuthenticatedAppShell />}>
                <Route path="/" element={<RoleHomeRedirect />} />
                <Route path="/dashboard" element={null} />
                <Route path="/live-map" element={null} />
                <Route path="/history" element={null} />
                <Route path="/import" element={null} />
                <Route path="/vehicles" element={null} />
                <Route element={<AdminOnlyRoute />}>
                  <Route path="/fleets" element={null} />
                  <Route path="/users" element={null} />
                </Route>
                <Route path="/gps-data" element={null} />
                <Route path="/campaigns" element={null} />
                <Route path="/impressions" element={null} />
                <Route path="/impression-jobs" element={null} />
                <Route path="/reports" element={null} />
                <Route path="/report-create" element={null} />
                <Route path="/settings" element={null} />
                <Route path="/profile" element={null} />
                <Route path="*" element={<RoleHomeRedirect />} />
              </Route>
            </Route>
          </Route>
        </Routes>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default App;
